import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Calendar, Sparkles, Moon, ShoppingBag, Play, MapPin, Building, Waves, Gift } from 'lucide-react';
import NewsletterForm from '@/components/NewsletterForm';
import LaVillaLogo from '@/components/LaVillaLogo';
import { useRef, useState, useEffect } from 'react';
import { isAperitivoBonusActive } from '@/data/giftcards2027';

// Real La Villa images
const heroImage = "/images/hero-main.jpg";
const poolImage = "/images/pool-day.jpg"; // Piscina di giorno
const eventsImage = "/images/pool-night-1.jpeg"; // Pool party night
const nightImage = "/images/pool-night-2.jpeg"; // Night event
const aerialImage = "/images/villa-aerial.jpeg"; // Vista aerea invernale

// Instagram Feed Component using Fouita
function InstagramFeed() {
  useEffect(() => {
    // Load Fouita widget
    if (!document.querySelector('#ft6ermcxvl')) {
      const newDiv = document.createElement('div');
      newDiv.setAttribute('key', 'Feed Instagram di Masonry');
      newDiv.setAttribute('class', 'ft');
      newDiv.setAttribute('id', 'ft6ermcxvl');
      const newScript = document.createElement('script');
      newScript.setAttribute('src', 'https://wdg.fouita.com/widgets/0x3f04fa.js');
      newDiv.appendChild(newScript);
      
      const container = document.getElementById('instagram-feed-container');
      if (container) {
        container.appendChild(newDiv);
      }
    }
  }, []);

  return (
    <div 
      id="instagram-feed-container" 
      className="min-h-[300px] md:min-h-auto overflow-x-auto md:overflow-x-visible scrollbar-hide"
      style={{
        WebkitOverflowScrolling: 'touch',
        scrollSnapType: 'x mandatory'
      }}
    ></div>
  );
}

// Countdown component - Calendario stilizzato con giorni mancanti
function CountdownTimer({ targetDate }) {
  const [daysLeft, setDaysLeft] = useState(0);
  
  useEffect(() => {
    const calculateDays = () => {
      const now = new Date();
      const target = new Date(targetDate);
      const diffTime = target - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDaysLeft(diffDays > 0 ? diffDays : 0);
    };
    
    calculateDays();
    
    // Update at midnight
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const msUntilMidnight = tomorrow - now;
    
    const midnightTimeout = setTimeout(() => {
      calculateDays();
      const dailyInterval = setInterval(calculateDays, 24 * 60 * 60 * 1000);
      return () => clearInterval(dailyInterval);
    }, msUntilMidnight);
    
    return () => clearTimeout(midnightTimeout);
  }, [targetDate]);
  
  return (
    <motion.span 
      className="inline-flex items-center gap-2"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.6, duration: 0.4 }}
      data-testid="countdown-timer"
    >
      {/* Icona calendario stilizzata con numero giorni dentro */}
      <span 
        className="relative inline-flex flex-col items-center justify-center w-12 h-12 bg-white/95 rounded shadow-lg"
        style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}
      >
        {/* Bordo rosso in alto */}
        <span className="absolute top-0 left-0 right-0 h-3 bg-red-600 rounded-t"></span>
        {/* Numero giorni con meno davanti */}
        <span className="font-heading text-lg font-bold text-night mt-1">-{daysLeft}</span>
        {/* Label giorni */}
        <span className="text-[6px] uppercase tracking-wider text-stone-500 -mt-1">giorni</span>
      </span>
    </motion.span>
  );
}

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" }
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <div className="bg-ivory" data-testid="home-page">
      {/* Hero Section with Parallax */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          <img
            src={heroImage}
            alt="La Villa pool at sunset"
            className="w-full h-full object-cover scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/5 to-ivory/70" />
        </motion.div>
        
        <motion.div className="relative z-10 text-center px-6 max-w-4xl mx-auto -mt-20 md:-mt-32" style={{ opacity: heroOpacity }}>
          {/* Logo in alto nel cielo */}
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            data-testid="hero-title"
          >
            <img 
              src="/images/logo-new.png" 
              alt="La Villa" 
              className="h-44 sm:h-56 md:h-96 lg:h-[28rem] mx-auto drop-shadow-2xl"
            />
          </motion.div>
          
          {/* Castelfranco */}
          <motion.div
            className="mb-4 flex flex-wrap items-center justify-center gap-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <p
              className="font-body text-[10px] sm:text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] text-white font-semibold"
              style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 0 20px rgba(0,0,0,0.5)' }}
            >
              Castelfranco Emilia - Stagione 2026
            </p>
          </motion.div>
          
          <motion.p
            className="font-accent text-base sm:text-lg md:text-2xl italic text-white mb-3 md:mb-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            style={{ textShadow: '0 2px 15px rgba(0,0,0,0.5)' }}
          >
            "Ogni gesto, una firma."
          </motion.p>
          
          <motion.p
            className="font-body text-sm sm:text-base md:text-lg text-white max-w-xl mx-auto mb-6 md:mb-10 font-medium px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            style={{ textShadow: '0 2px 15px rgba(0,0,0,0.5)' }}
          >
            Oltre 30 anni di storia. Piscina, eventi, nightlife.<br />
            Un'esperienza sensoriale nel cuore dell'Emilia.
          </motion.p>
          
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Button
              asChild
              className="bg-night text-ivory hover:bg-stone-800 rounded-full px-5 py-4 sm:px-6 sm:py-5 md:px-8 md:py-6 text-xs sm:text-sm uppercase tracking-widest group"
              data-testid="hero-cta-abbonamenti"
            >
              <Link to="/abbonamenti">
                Abbonamenti <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={14} />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-night text-night hover:bg-night hover:text-ivory rounded-full px-5 py-4 sm:px-6 sm:py-5 md:px-8 md:py-6 text-xs sm:text-sm uppercase tracking-widest"
              data-testid="hero-cta-eventi"
            >
              <Link to="/eventi-privati">Prenota Evento</Link>
            </Button>
          </motion.div>
        </motion.div>
        
        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <div className="w-6 h-10 border-2 border-night/30 rounded-full flex justify-center">
            <motion.div
              className="w-1.5 h-3 bg-champagne rounded-full mt-2"
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            />
          </div>
        </motion.div>
      </section>
      
      {/* Three Pillars Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="pillars-section">
        <div className="max-w-[1800px] mx-auto">
          <div className="text-center mb-16">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-champagne mb-4">
              Scopri
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-night tracking-tight flex items-center justify-center gap-3 overflow-hidden">
              <motion.span
                initial={{ x: 100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                L'Esperienza
              </motion.span>
              <motion.span
                initial={{ x: -100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
              >
                <LaVillaLogo size="2xl" className="h-9 md:h-10" dark={true} />
              </motion.span>
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {/* il Giorno */}
            <motion.div
              className="group relative overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="/images/il-giorno.jpg"
                  alt="il Giorno - La Villa"
                  className="w-full h-full object-cover image-hover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-night/80 to-transparent flex flex-col justify-end p-3 sm:p-4 md:p-6">
                <Sparkles className="text-champagne mb-1 sm:mb-2 md:mb-3" size={18} strokeWidth={1.5} />
                <h3 className="font-heading text-sm sm:text-base md:text-xl text-ivory mb-1 md:mb-2">il Giorno</h3>
                <p className="font-body text-[10px] sm:text-xs text-stone-300 mb-2 md:mb-3 line-clamp-2">
                  Relax immersi nel verde, sotto il sole dell'Emilia.
                </p>
                <Link
                  to="/la-villa"
                  className="font-body text-[10px] sm:text-xs uppercase tracking-widest text-champagne hover:text-ivory transition-colors inline-flex items-center gap-1 sm:gap-2"
                  data-testid="pillar-day-link"
                >
                  Scopri <ArrowRight size={12} />
                </Link>
              </div>
            </motion.div>
            
            {/* THE EVENING */}
            <motion.div
              className="group relative overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={nightImage}
                  alt="The Evening - Night Events"
                  className="w-full h-full object-cover image-hover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-night/80 to-transparent flex flex-col justify-end p-3 sm:p-4 md:p-6">
                <Moon className="text-champagne mb-1 sm:mb-2 md:mb-3" size={18} strokeWidth={1.5} />
                <h3 className="font-heading text-sm sm:text-base md:text-xl text-ivory mb-1 md:mb-2">La Sera</h3>
                <p className="font-body text-[10px] sm:text-xs text-stone-300 mb-2 md:mb-3 line-clamp-2">
                  Serate esclusive, musica selezionata, atmosfera unica.
                </p>
                <Link
                  to="/night-events"
                  className="font-body text-[10px] sm:text-xs uppercase tracking-widest text-champagne hover:text-ivory transition-colors inline-flex items-center gap-1 sm:gap-2"
                  data-testid="pillar-evening-link"
                >
                  Calendario <ArrowRight size={12} />
                </Link>
              </div>
            </motion.div>
            
            {/* il Tuo Evento */}
            <motion.div
              className="group relative overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="/images/il-tuo-evento.jpg"
                  alt="il Tuo Evento - Private Events"
                  className="w-full h-full object-cover image-hover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-night/80 to-transparent flex flex-col justify-end p-3 sm:p-4 md:p-6">
                <Calendar className="text-champagne mb-1 sm:mb-2 md:mb-3" size={18} strokeWidth={1.5} />
                <h3 className="font-heading text-sm sm:text-base md:text-xl text-ivory mb-1 md:mb-2">il Tuo Evento</h3>
                <p className="font-body text-[10px] sm:text-xs text-stone-300 mb-2 md:mb-3 line-clamp-2">
                  Compleanni, lauree, eventi aziendali. Il tuo momento speciale.
                </p>
                <Link
                  to="/eventi-privati"
                  className="font-body text-[10px] sm:text-xs uppercase tracking-widest text-champagne hover:text-ivory transition-colors inline-flex items-center gap-1 sm:gap-2"
                  data-testid="pillar-private-link"
                >
                  Prenota <ArrowRight size={12} />
                </Link>
              </div>
            </motion.div>
            
            {/* La Collezione */}
            <motion.div
              className="group relative overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="/images/la-collezione.png"
                  alt="La Collezione - La Villa Shop"
                  className="w-full h-full object-cover image-hover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-night/80 to-transparent flex flex-col justify-end p-3 sm:p-4 md:p-6">
                <ShoppingBag className="text-champagne mb-1 sm:mb-2 md:mb-3" size={18} strokeWidth={1.5} />
                <h3 className="font-heading text-sm sm:text-base md:text-xl text-ivory mb-1 md:mb-2">La Collezione</h3>
                <p className="font-body text-[10px] sm:text-xs text-stone-300 mb-2 md:mb-3 line-clamp-2">
                  Teli, costumi e accessori firmati La Villa.
                </p>
                <Link
                  to="/shop"
                  className="font-body text-[10px] sm:text-xs uppercase tracking-widest text-champagne hover:text-ivory transition-colors inline-flex items-center gap-1 sm:gap-2"
                  data-testid="pillar-collection-link"
                >
                  Shop <ArrowRight size={12} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* La Villa in 30 Seconds */}
      <section className="py-16 md:py-24 lg:py-32 bg-stone-100 overflow-hidden" data-testid="about-section">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <motion.p 
                className="font-body text-[10px] sm:text-xs uppercase tracking-[0.2em] text-champagne mb-3 md:mb-4"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                Benvenuto
              </motion.p>
              
              {/* Mobile: Content + Vertical Video Side by Side */}
              <div className="flex gap-3 sm:gap-4 lg:block">
                {/* Text content */}
                <motion.div 
                  className="flex-1"
                  initial={{ opacity: 0, x: -80 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
                >
                  <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-night tracking-tight mb-4 md:mb-8">
                    <LaVillaLogo size="2xl" className="sm:h-10 md:h-12 lg:h-16" dark={true} /> in<br />30 Secondi
                  </h2>
                  
                  <div className="space-y-3 sm:space-y-4 md:space-y-6 mb-6 md:mb-8 font-body text-sm sm:text-base text-stone-600 leading-relaxed">
                <p>
                  Una dimora immersa nel verde,<br />
                  tra Modena e Bologna.
                </p>
                <p>
                  Architettura elegante senza tempo, ampi spazi interni,<br />
                  giardino perimetrato di oltre 2.000 mq,<br />
                  piscina scenografica incastonata nel prato.
                </p>
                <p>
                  Portici, zone lounge, area bar dedicata,<br />
                  spazi modulabili tra interno ed esterno.<br />
                  Ogni angolo pensato per vivere l'estate<br />
                  con stile e riservatezza.
                </p>
                <p>
                  <strong className="text-night">Di giorno:</strong><br />
                  sole, acqua, cocktail signature,<br />
                  atmosfera resort.
                </p>
                <p>
                  <strong className="text-night">Al tramonto:</strong><br />
                  aperitivi dorati, musica selezionata,<br />
                  energia che cambia lentamente ritmo.
                </p>
                <p>
                  <strong className="text-night">Di sera:</strong><br />
                  eventi privati su misura,<br />
                  nightlife elegante a capienza controllata.
                </p>
                <p>
                  Compleanni, lauree, corporate event,<br />
                  esperienze costruite attorno a te.
                </p>
                <p className="text-night font-medium italic flex flex-wrap items-center gap-1">
                  <LaVillaLogo size="sm" dark={true} /> non è un semplice spazio.<br />
                  È un club riservato a chi cerca qualcosa di diverso.
                </p>
              </div>
              <Button
                asChild
                className="bg-night text-ivory hover:bg-stone-800 rounded-full px-5 py-4 sm:px-6 sm:py-5 md:px-8 md:py-6 text-xs sm:text-sm uppercase tracking-widest"
                data-testid="about-cta"
              >
                <Link to="/la-villa">
                  Scopri di Più <ArrowRight className="ml-2" size={14} />
                </Link>
              </Button>
                </motion.div>
                
                {/* Vertical video on mobile - spans full text height */}
                <motion.div 
                  className="lg:hidden w-40 sm:w-44 flex-shrink-0 self-stretch overflow-hidden rounded-lg shadow-lg border border-champagne/30"
                  initial={{ opacity: 0, x: 80 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                >
                  <video
                    src="/videos/champagne.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: '31% center' }}
                    data-testid="champagne-video-mobile"
                  />
                </motion.div>
              </div>
            </div>
            
            {/* Large video on desktop - hidden on mobile */}
            <motion.div
              className="relative hidden lg:block"
              initial={{ opacity: 0, x: 100 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            >
              <div className="overflow-hidden rounded-lg shadow-lg">
                <video
                  src="/videos/champagne.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto"
                  data-testid="champagne-video"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Natale — Gift Card */}
      {isAperitivoBonusActive() && (
        <section className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-night relative overflow-hidden" data-testid="christmas-giftcard-banner">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-champagne/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-champagne/5 blur-3xl" />
          <div className="relative max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center justify-center gap-2 mb-4">
                <Gift size={18} className="text-champagne" />
                <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne">Natale alla Villa</p>
              </div>
              <h2 className="font-heading text-4xl md:text-5xl text-ivory tracking-tight mb-6">
                Quest'anno, regala un'estate.
              </h2>
              <p className="font-body text-stone-300 leading-relaxed max-w-2xl mx-auto mb-4">
                La Villa Gift Card: un credito da vivere in piscina nella stagione 2027, con dedica personalizzata
                e consegna immediata via email.
              </p>
              <p className="font-body text-sm text-champagne mb-10">
                Fino al 24 dicembre, la card da 200 € include 20 € di bonus dedicati all'aperitivo.
              </p>
              <Button
                asChild
                className="bg-champagne text-night hover:bg-ivory rounded-full px-8 py-6 text-sm uppercase tracking-widest"
                data-testid="christmas-giftcard-cta"
              >
                <Link to="/abbonamenti#gift-card">
                  Scopri le Gift Card <ArrowRight className="ml-2" size={16} />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>
      )}

      {/* Summer 2027 — The Preview */}
      <section className="relative py-28 md:py-40 px-6 md:px-12 lg:px-24 overflow-hidden" data-testid="summer-2027-teaser">
        <div className="absolute inset-0">
          <img
            src="/images/pool-day.jpg"
            alt="La piscina de La Villa"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-night/95 via-night/75 to-night/40" />
        </div>
        <div className="relative max-w-[1800px] mx-auto">
          <div className="max-w-2xl">
            <motion.p
              className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Summer 2027 — The Preview
            </motion.p>
            <motion.h2
              className="font-heading text-4xl md:text-5xl text-ivory tracking-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              La tua prossima estate comincia qui.
            </motion.h2>
            <motion.p
              className="font-body text-stone-300 leading-relaxed mb-10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Piscina, palestra all'aperto, Jacuzzi sotto le stelle, cinema estivo e nuove esperienze da scoprire.
              Entra nell'anteprima dell'estate 2027 alla Villa.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Button
                asChild
                className="bg-champagne text-night hover:bg-ivory rounded-full px-8 py-6 text-sm uppercase tracking-widest"
                data-testid="summer-2027-cta"
              >
                <Link to="/estate-2027">
                  Scopri l'estate 2027 <ArrowRight className="ml-2" size={16} />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-ivory/60 bg-transparent text-ivory hover:bg-ivory hover:text-night rounded-full px-8 py-6 text-sm uppercase tracking-widest whitespace-normal h-auto"
                data-testid="summer-2027-eventi-cta"
              >
                <Link to="/eventi-privati">
                  Organizza un evento privato
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Shop Teaser */}
      <section className="py-24 md:py-32 bg-stone-100" data-testid="shop-teaser">
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <p className="font-body text-xs uppercase tracking-[0.2em] text-champagne mb-4">
                Limited Edition
              </p>
              <h2 className="font-heading text-4xl md:text-5xl text-night tracking-tight flex items-center gap-3">
                Shop <LaVillaLogo size="3xl" dark={true} />
              </h2>
            </div>
            <Link
              to="/shop"
              className="font-body text-sm uppercase tracking-widest text-night hover:text-champagne transition-colors inline-flex items-center gap-2 mt-4 md:mt-0"
              data-testid="shop-link"
            >
              Vedi Tutto <ArrowRight size={16} />
            </Link>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { name: 'Telo Mare', img: 'https://images.unsplash.com/photo-1760722974657-f64bce2f9cc5?w=400' },
              { name: 'Costume Uomo', img: 'https://images.unsplash.com/photo-1761964699631-f11c1732f2ee?w=400' },
              { name: 'Bikini Donna', img: 'https://images.unsplash.com/photo-1570976447640-ac859083963f?w=400' },
              { name: 'Cappellino', img: 'https://customer-assets.emergentagent.com/job_3c29091e-d480-4fac-abcd-7dae870cdfe6/artifacts/ytdlensk_cappellino.png' },
            ].map((product, i) => (
              <motion.div
                key={product.name}
                className="group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="aspect-[3/4] overflow-hidden bg-white mb-4">
                  <img
                    src={product.img}
                    alt={product.name}
                    className="w-full h-full object-cover image-hover"
                  />
                </div>
                <p className="font-body text-sm text-night">{product.name}</p>
                <p className="font-body text-xs text-stone-500 blur-sm select-none">TBA</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Newsletter Section */}
      <section className="py-24 md:py-32 bg-night" data-testid="newsletter-section">
        <div className="max-w-xl mx-auto px-6 text-center">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-champagne mb-4">
            Inner Circle
          </p>
          <h2 className="font-heading text-3xl md:text-4xl text-ivory tracking-tight mb-4">
            Entra nel Club
          </h2>
          <p className="font-body text-stone-400 mb-8">
            Ricevi in anteprima eventi esclusivi, promozioni e novità dalla Villa.
          </p>
          <NewsletterForm />
        </div>
      </section>
      
      {/* Virtual Tour 3D Banner */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 overflow-hidden" data-testid="virtual-tour-banner">
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1762254928684-477525f48207?w=1920&q=80&auto=format)',
            filter: 'blur(3px) brightness(0.25) saturate(1.2)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />
        
        <div className="relative max-w-[1800px] mx-auto flex flex-col md:flex-row items-center justify-between gap-12">
          <div className="text-center md:text-left max-w-xl">
            <motion.p
              className="font-body text-[10px] tracking-[0.3em] uppercase text-amber-300/60 mb-4"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Coming Soon
            </motion.p>
            <motion.h2
              className="font-heading text-3xl md:text-4xl lg:text-5xl text-ivory tracking-tight mb-4"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              style={{ textShadow: '0 0 40px rgba(212,175,55,0.1)' }}
            >
              Esplora La Villa in 3D
            </motion.h2>
            <motion.p
              className="font-body text-sm text-white/40 leading-relaxed mb-8"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Cammina nel nostro spazio, scegli il tuo tavolo e prenota la tua esperienza. 
              Il tour virtuale interattivo sta arrivando.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Link
                to="/virtual-tour"
                className="inline-block px-8 py-3 border border-white/20 hover:border-amber-300/40 text-white/60 hover:text-amber-200 font-body text-xs tracking-[0.2em] uppercase transition-all duration-500"
                data-testid="virtual-tour-cta"
              >
                Anteprima Tour 3D
              </Link>
            </motion.div>
          </div>
          
          {/* Preview card */}
          <motion.div
            className="relative w-full md:w-96 h-56 md:h-64 overflow-hidden border border-white/10"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: 'url(https://images.pexels.com/photos/3048211/pexels-photo-3048211.jpeg?auto=compress&cs=tinysrgb&w=800)',
                filter: 'blur(2px) brightness(0.4)',
              }}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-heading text-lg text-ivory/80 tracking-wide">ENTRA A</span>
                <img src="/images/logo-new-white.png" alt="La Villa" className="h-5 opacity-80" />
              </div>
              <p className="font-body text-[9px] tracking-[0.2em] uppercase text-amber-300/40">Tour Interattivo</p>
            </div>
            {/* Fake hotspots */}
            {[
              { x: 25, y: 35 }, { x: 60, y: 50 }, { x: 80, y: 30 },
            ].map((p, idx) => (
              <span
                key={`hp-${idx}`}
                className="absolute w-2 h-2 rounded-full bg-white/20 border border-white/10 animate-pulse"
                style={{ left: `${p.x}%`, top: `${p.y}%` }}
              />
            ))}
          </motion.div>
        </div>
      </section>
      
      {/* Instagram Feed Section - Fouita Free Widget */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-stone-50" data-testid="social-section">
        <div className="max-w-[1800px] mx-auto">
          <div className="text-center mb-12">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-champagne mb-4">
              @la_villa.cf
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-night tracking-tight mb-4">
              Seguici su Instagram
            </h2>
            <p className="font-body text-stone-500 max-w-lg mx-auto">
              Scopri i momenti più belli de <LaVillaLogo size="sm" dark={true} className="inline-block align-middle" />. Segui il nostro profilo per restare aggiornato su eventi, atmosfere e novità.
            </p>
          </div>
          
          {/* Fouita Instagram Feed Widget */}
          <InstagramFeed />
        </div>
      </section>
      
      {/* Bottom padding for mobile CTA */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}
